import { useLayoutEffect } from "react";
import { View, useWindowDimensions } from "react-native";
import Animated, { SlideInLeft, SlideInRight, SlideOutLeft, SlideOutRight } from "react-native-reanimated";
import { Stack, useRouter } from "expo-router";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import { ClipCompilationPage, CompilationWatchView, RequestCompilationForm } from "@visioni/a3s-media/app";
import { DialogModal } from "@src/components/DialogModal";
import { useClipCompilationEnabled } from "@src/features/clip-compilation/hooks/useClipCompilationEnabled";
import { useClipCompilationPageProps } from "@src/features/clip-compilation/hooks/useClipCompilationPageProps";
import { useCompilationWatch } from "@src/features/clip-compilation/hooks/useCompilationWatch";
import { useIsFirstRender } from "@src/hooks/useIsFirstRender";
import { CompilationVideoPlayer } from "@src/features/clip-compilation/components/CompilationVideoPlayer";

const styles = {
  flex: { flex: 1 },
  // Clips the slide transition to the content area — without this, a view
  // translating in/out visually overlaps the side nav rather than being
  // hidden behind its edge.
  clip: { flex: 1, overflow: "hidden" },
} as const;

/**
 * Access gate. The screen's data hooks live in `ClipCompilationContent` so that
 * mounting them is itself gated — rendering the content and redirecting
 * afterwards would flash the restricted UI and fire the list, sites and camera
 * queries for a user on their way out.
 */
export default function ClipCompilationScreen() {
  const router = useRouter();
  const isEnabled = useClipCompilationEnabled();

  useLayoutEffect(() => {
    if (!isEnabled) {
      router.replace("/overview");
    }
  }, [isEnabled, router]);

  if (!isEnabled) return null;

  return <ClipCompilationContent />;
}

function ClipCompilationContent() {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const { pageProps, isRequestDialogOpen, closeRequestDialog, requestFormProps, watchingItem, closeWatchView } =
    useClipCompilationPageProps();

  const { clip, handleDownload } = useCompilationWatch(watchingItem);
  // The slide is for moving between the list and the watch view; on the
  // screen's own first paint there is nothing to move from.
  const isFirstRender = useIsFirstRender();

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <WebLayout>
        <View style={styles.clip}>
          {watchingItem ?
            <Animated.View
              key="watch"
              entering={isFirstRender ? undefined : SlideInRight}
              exiting={SlideOutRight}
              style={styles.flex}
            >
              <CompilationWatchView
                cameraLabel={watchingItem.cameraLabel}
                dateRange={`${watchingItem.rangeStart} – ${watchingItem.rangeEnd}`}
                onBack={closeWatchView}
                onDownload={handleDownload}
              >
                <CompilationVideoPlayer clip={clip} fillParent />
              </CompilationWatchView>
            </Animated.View>
          : <Animated.View
              key="list"
              entering={isFirstRender ? undefined : SlideInLeft}
              exiting={SlideOutLeft}
              style={styles.flex}
            >
              <ClipCompilationPage {...pageProps} />
            </Animated.View>
          }
        </View>
        <DialogModal
          visible={isRequestDialogOpen}
          onClose={closeRequestDialog}
          title="Request compilation"
          style={{ width: 480, maxWidth: "100%" }}
        >
          <RequestCompilationForm {...requestFormProps} />
        </DialogModal>
      </WebLayout>
    </>
  );
}
